import { fetchWithAuth, getApiBaseUrl } from './api';
import { ensureOk, okJson } from './httpError';

export interface HikvisionLPRCamera {
  id: string;
  customer_id: string;
  name: string;
  device_ip: string;
  device_port: number;
  username: string;
  password: string;
  channel_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HikvisionLPRCameraCreate {
  customer_id: string;
  name: string;
  device_ip?: string;
  device_port?: number;
  username?: string;
  password?: string;
  channel_id?: number;
}

export interface HikvisionLPRCameraUpdate {
  name?: string;
  device_ip?: string;
  device_port?: number;
  username?: string;
  password?: string;
  channel_id?: number;
  is_active?: boolean;
}

export interface HikvisionLPRStatus {
  camera_id: string;
  active: boolean;
  last_plate?: string;
  last_plate_at?: string;
}

const base = () => getApiBaseUrl();

// CRUD — via condo-manager
export async function listHikvisionLPRCameras(customerId: string): Promise<HikvisionLPRCamera[]> {
  const res = await fetchWithAuth(`${base()}/api/v1/hikvision-lpr-cameras?customer_id=${customerId}`);
  // Unchecked, an error body fell through `json.data ?? json` and was returned as the camera list.
  const json = await okJson<any>(res, 'Erro ao carregar as câmeras LPR Hikvision');
  return json.data ?? json;
}

export async function createHikvisionLPRCamera(data: HikvisionLPRCameraCreate): Promise<HikvisionLPRCamera> {
  const { customer_id, ...body } = data;
  const res = await fetchWithAuth(`${base()}/api/v1/hikvision-lpr-cameras?customer_id=${customer_id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateHikvisionLPRCamera(id: string, data: HikvisionLPRCameraUpdate): Promise<HikvisionLPRCamera> {
  const res = await fetchWithAuth(`${base()}/api/v1/hikvision-lpr-cameras/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function deleteHikvisionLPRCamera(id: string): Promise<void> {
  const res = await fetchWithAuth(`${base()}/api/v1/hikvision-lpr-cameras/${id}`, { method: 'DELETE' });
  // The response was discarded, so a failed delete resolved as success.
  await ensureOk(res, 'Erro ao remover a câmera');
}

// Manual barrier open — direct ISAPI via iot-connect
export async function openHikvisionDoor(cameraId: string): Promise<void> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/hikvision-lpr/${cameraId}/open-door`, {
    method: 'POST',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }
}

// Status — via iot-connect
export async function getHikvisionLPRStatus(cameraId: string): Promise<HikvisionLPRStatus> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/hikvision-lpr/${cameraId}/status`);
  return okJson(res, 'A câmera não respondeu. Verifique se o equipamento está online.');
}
